import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Soft-nudge banner shown to monthly subscribers after 60+ days on plan.
 * Server decides eligibility (`should_suggest_annual`) — frontend just renders + persists dismissal.
 * Dismissals re-surface after 30 days so we don't nag, but we also don't lose the revenue lift.
 */
const DISMISS_KEY = 'vh_annual_upsell_dismissed_at';
const REDISPLAY_AFTER_DAYS = 30;

export default function AnnualUpsellBanner() {
  const [billing, setBilling] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    axios.get(`${API}/me/billing`)
      .then((r) => setBilling(r.data))
      .catch(() => {});

    const raw = window.localStorage.getItem(DISMISS_KEY);
    if (raw) {
      try {
        const when = new Date(raw);
        const ageDays = (Date.now() - when.getTime()) / 86400000;
        if (ageDays < REDISPLAY_AFTER_DAYS) setDismissed(true);
      } catch (e) {
        // corrupt timestamp — treat as not dismissed; banner will show again
      }
    }
  }, []);

  if (!billing || dismissed) return null;
  const sub = billing.subscription;
  if (!sub?.should_suggest_annual) return null;

  function dismiss() {
    try {
      window.localStorage.setItem(DISMISS_KEY, new Date().toISOString());
    } catch (e) {
      // quota exceeded — just hide for this session
    }
    setDismissed(true);
  }

  return (
    <div
      data-testid="annual-upsell-banner"
      className="mb-6 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 text-white p-5 shadow-lg flex flex-wrap items-center gap-4 justify-between"
    >
      <div className="flex items-center gap-4 flex-1 min-w-[260px]">
        <div className="text-3xl">💸</div>
        <div>
          <div className="font-semibold text-lg" data-testid="annual-upsell-title">
            Save {sub.annual_savings_pct}% — switch to annual billing
          </div>
          <div className="text-sm text-emerald-50">
            You've been on the {sub.tier_name} monthly plan for {sub.days_on_plan} days.
            Switching to annual locks in {sub.annual_savings_pct}% off and gives you predictable cash flow.
          </div>
        </div>
      </div>
      <div className="flex gap-2">
        <Link
          to="/pricing"
          data-testid="annual-upsell-cta"
          className="px-5 py-2 rounded-lg bg-white text-emerald-700 font-semibold hover:bg-emerald-50 transition"
        >
          See annual pricing
        </Link>
        <button
          onClick={dismiss}
          data-testid="annual-upsell-dismiss"
          className="px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 transition text-sm"
          title="Dismiss for 30 days"
        >
          Not now
        </button>
      </div>
    </div>
  );
}
